/**
 * Custom-ingredient commands — the user's private fermentable/hop/yeast/misc
 * library.
 *
 * Four commands, the create/update/delete/restore shape inventory already
 * established:
 *
 * | Command | Shape it follows |
 * | --- | --- |
 * | `customIngredient.add` | `inventory.add` — a required `from` document |
 * | `customIngredient.update` | `inventory.update` — a partial `changes` |
 * | `customIngredient.remove` | `inventory.remove` — inverted with the whole prior document, plus the lots a cascade took |
 * | `customIngredient.restore` | `inventory.restore` — an upsert, of the entry and of those lots |
 *
 * ## What an entry owns beyond itself
 *
 * A *lot* linked to an entry (`InventoryItemDoc.ingredientId`) takes its name
 * and maker from the entry: `customIngredient.update` rewrites them on every
 * linked lot, and nothing else. A *recipe row* linked to an entry keeps the
 * name it was written with, exactly as a catalog-backed row does — the link is
 * identity for matching, not a live binding (D-IL2) — and a frozen batch keeps
 * everything (D-IL3). `customIngredient.remove` refuses while a lot or a live
 * recipe row still points at the entry (B2), with one cascade for lots that are
 * empty (B3); see {@link customIngredientRemove}.
 */

import type { CustomIngredientDoc } from '../../document/custom-ingredient';
import { customIngredientDocSchema, customIngredientMaker } from '../../document/custom-ingredient';
import type { InventoryItemDoc } from '../../document/inventory';
import type { WorkspaceState } from '../../document/workspace';
import { type CustomIngredientUsage, customIngredientUsage } from '../../inventory/links';
import { CUSTOM_INGREDIENT_SCHEMA_VERSION } from '../../version';
import { defineCommand, inverseCommand } from '../definition';
import { commandError } from '../envelope';
import { quoted } from '../format';
import { mergeDefined, priorValues } from '../helpers';
import * as p from '../payloads';

/** A copy of `state` with one library entry replaced or added. */
function putItem(state: WorkspaceState, item: CustomIngredientDoc): WorkspaceState {
  return { ...state, customIngredients: { ...state.customIngredients, [item.id]: item } };
}

/** Look an item up for a summary, tolerating its absence. */
function itemName(state: WorkspaceState, ingredientId: string): string {
  return state.customIngredients[ingredientId]?.name ?? ingredientId;
}

/**
 * Rewrite the name and maker of every lot linked to `ingredient`.
 *
 * Lots, and **only** lots. A recipe row linked to the same entry keeps the name
 * it was written with — that is the deliberate rule (D-IL2), not an omission to
 * be tidied up later: a recipe row has no `maker` column (the maker is folded
 * into its name by `customIngredientDisplayName`), so propagating there would
 * need a second, per-target naming rule, and it would silently reword recipes
 * the brewer may already have brewed from. Frozen history — `state.batches`,
 * `state.recipeSnapshots` — is untouchable for the same reason a snapshot is
 * frozen at all (D-IL3): a batch that says "brewed as v3" has to keep saying
 * what it said.
 *
 * The lots' `updatedAt` is deliberately left alone. The lot was not edited —
 * the ingredient was — and stamping it would also break the undo identity:
 * the inverse rename carries the *ingredient's* prior `updatedAt` as its
 * envelope timestamp, not each lot's, so a stamped lot would come back from
 * undo wearing the wrong time. Lots whose label already matches are returned
 * by reference, and when none changes the inventory table itself is shared,
 * so a spec edit to the entry costs the cupboard no allocation at all.
 *
 * ## The identity is conditional, and this is the condition
 *
 * That skip is what makes the undo identity conditional, so read it before
 * reasoning about `apply(invert(s, c), apply(s, c)) === s` here. Both
 * directions assume the invariant `inventory.add` establishes: **a linked lot's
 * label already equals its entry's.** Under it the round trip is exact — the
 * forward rename rewrites the lot, the inverse rename rewrites it back — and
 * that holds equally when only the maker moves, and for a lot linked to some
 * other entry, which neither direction touches.
 *
 * A lot that has *diverged* from its entry is the exception, and it does not
 * survive the round trip: if its label happens to equal the new name already,
 * the forward pass skips it and the inverse then rewrites it to the entry's old
 * label, so the lot's own spelling is lost. Divergence is reachable today
 * through `inventory.restore` — delete a linked lot, rename the entry while it
 * is gone (nothing propagates; there is no lot), then undo the delete and the
 * lot returns with a stale label. Closing that would mean relabelling on
 * restore, or refusing `name`/`maker` on a linked lot in `inventory.update`,
 * and each only trades this hole for one in restore's own inverse; B1's scope
 * is `add` and `update`. So the condition is written down here rather than
 * engineered away, and the next reader finds it instead of rediscovering it.
 */
function relabelLinkedLots(state: WorkspaceState, ingredient: CustomIngredientDoc): WorkspaceState {
  const maker = customIngredientMaker(ingredient);
  let inventory: Record<string, InventoryItemDoc> | null = null;
  for (const lot of Object.values(state.inventory)) {
    if (lot.ingredientId !== ingredient.id) continue;
    if (lot.name === ingredient.name && lot.maker === maker) continue;
    inventory ??= { ...state.inventory };
    inventory[lot.id] = { ...lot, name: ingredient.name, maker };
  }
  return inventory === null ? state : { ...state, inventory };
}

/** `2 lots`, `1 recipe` — how a count reads in a refusal. */
function counted(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/**
 * Why a removal was refused, with the counts.
 *
 * The dialog has the workspace and asks {@link customIngredientUsage} itself;
 * this sentence is for the callers that do not — the assistant reading a
 * rejected proposal, the sync log, a toast on a device that raced. Recipes are
 * named first because they are the half no cascade can clear.
 */
function inUseMessage(
  name: string,
  usage: CustomIngredientUsage,
  cascade: 'none' | 'empty-lots',
): string {
  const parts: string[] = [];
  if (usage.recipes.length > 0) parts.push(counted(usage.recipes.length, 'recipe'));
  const lots = cascade === 'empty-lots' ? usage.lots.filter((lot) => lot.quantity > 0) : usage.lots;
  if (lots.length > 0) {
    parts.push(`${counted(lots.length, 'lot')}${cascade === 'empty-lots' ? ' with stock' : ''}`);
  }
  return `${quoted(name)} is still used by ${parts.join(' and ')}.`;
}

/**
 * The lots a cascade takes with the entry: the empty linked ones, in id order,
 * or none.
 *
 * The single expression of "what this removal takes", called by `apply`, by
 * `invert` and by `summarize`. They are the two halves of the undo identity
 * plus the sentence describing it, and if any of the three spelled the rule out
 * for itself they could drift without a test being able to see it: `apply`
 * reaches the deletion only when nothing with stock is linked, so there
 * "every linked lot" and "every empty linked lot" happen to name the same set,
 * and a divergence there would be invisible until the day the guard changed.
 *
 * Takes the usage rather than the state so `apply`, which has already scanned,
 * does not scan a second time. A fresh, mutable array every call, because the
 * inverse hands it straight to the payload and `z.array` infers a mutable
 * element list; the copy is what keeps a `readonly` view of the workspace's own
 * lots from leaking into a command anyone may later reshape.
 */
function cascadedLots(
  usage: CustomIngredientUsage,
  cascade: 'none' | 'empty-lots',
): InventoryItemDoc[] {
  return cascade === 'empty-lots' ? usage.lots.filter((lot) => lot.quantity <= 0) : [];
}

/** Add a custom ingredient from a fully-built document. */
export const customIngredientAdd = defineCommand({
  type: 'customIngredient.add',
  payload: p.customIngredientAddPayloadSchema,
  apply: (state, command) => {
    const { ingredientId, from, name } = command.payload;
    if (state.customIngredients[ingredientId] !== undefined) {
      return {
        ok: false,
        error: commandError(
          'conflict',
          `A custom ingredient with id "${ingredientId}" already exists.`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    const doc = {
      ...from,
      schemaVersion: CUSTOM_INGREDIENT_SCHEMA_VERSION,
      id: ingredientId,
      name: name ?? from.name,
      createdAt: command.timestamp,
      updatedAt: command.timestamp,
    };
    const parsed = customIngredientDocSchema.safeParse(doc);
    if (!parsed.success) {
      return {
        ok: false,
        error: commandError('invalid-payload', 'Custom ingredient document is not valid.', {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    return { ok: true, state: putItem(state, parsed.data) };
  },
  invert: (_state, command) =>
    inverseCommand(
      command,
      'customIngredient.remove',
      { ingredientId: command.payload.ingredientId },
      command.timestamp,
    ),
  summarize: (_state, command) =>
    `Add custom ${command.payload.from.kind} ${quoted(command.payload.name ?? command.payload.from.name)}`,
});

/** Change fields of one library entry. */
export const customIngredientUpdate = defineCommand({
  type: 'customIngredient.update',
  payload: p.customIngredientUpdatePayloadSchema,
  apply: (state, command) => {
    const item = state.customIngredients[command.payload.ingredientId];
    if (item === undefined) {
      return {
        ok: false,
        error: commandError(
          'not-found',
          `No custom ingredient with id "${command.payload.ingredientId}".`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    const merged = mergeDefined(item, command.payload.changes);
    const parsed = customIngredientDocSchema.safeParse(merged);
    if (!parsed.success) {
      return {
        ok: false,
        error: commandError(
          'invalid-state',
          `Those changes would not produce a valid ${item.kind}.`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    const next = { ...parsed.data, updatedAt: command.timestamp };
    return { ok: true, state: relabelLinkedLots(putItem(state, next), next) };
  },
  invert: (state, command) => {
    const item = state.customIngredients[command.payload.ingredientId];
    return item === undefined
      ? null
      : inverseCommand(
          command,
          'customIngredient.update',
          {
            ingredientId: item.id,
            changes: priorValues(item, command.payload.changes),
          },
          item.updatedAt,
        );
  },
  summarize: (state, command) => {
    const keys = Object.keys(command.payload.changes).sort().join(', ');
    return `Update custom ${quoted(itemName(state, command.payload.ingredientId))}${
      keys === '' ? '' : ` (${keys})`
    }`;
  },
});

/**
 * Remove a library entry, refusing while anything still points at it.
 *
 * Invertible: the inverse is `customIngredient.restore` carrying the whole
 * prior document and, when the cascade took lots, those lots — so one undo puts
 * everything back and the identity holds without a batch.
 */
export const customIngredientRemove = defineCommand({
  type: 'customIngredient.remove',
  payload: p.customIngredientRemovePayloadSchema,
  apply: (state, command) => {
    const { ingredientId } = command.payload;
    const cascade = command.payload.cascade ?? 'none';
    const ingredient = state.customIngredients[ingredientId];
    if (ingredient === undefined) {
      return {
        ok: false,
        error: commandError('not-found', `No custom ingredient with id "${ingredientId}".`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    // The guard and the cascade read the same answer, so what the cascade takes
    // is exactly what the guard stopped looking at: under `empty-lots` only a
    // lot with stock still blocks, and recipes block under every mode.
    const usage = customIngredientUsage(state, ingredientId);
    // The inverse carries every lot it took, and the restore payload holds at
    // most CASCADE_LOT_LIMIT of them. A removal whose undo could not fit would
    // be a deletion with no way back — the store stacks an inverse without
    // validating it, so the failure would surface as "Undo failed" long after
    // the lots were gone — so it is refused instead of half-invertible.
    if (cascade === 'empty-lots' && usage.lots.length > p.CASCADE_LOT_LIMIT) {
      return {
        ok: false,
        error: commandError(
          'in-use',
          `${quoted(ingredient.name)} is still used by ${counted(usage.lots.length, 'lot')} — we can only remove up to ${p.CASCADE_LOT_LIMIT} at once and still let you undo it. Delete some of those lots first.`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    const blockingLots =
      cascade === 'empty-lots' ? usage.lots.filter((lot) => lot.quantity > 0) : usage.lots;
    if (usage.recipes.length > 0 || blockingLots.length > 0) {
      return {
        ok: false,
        error: commandError('in-use', inUseMessage(ingredient.name, usage, cascade), {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    const customIngredients = { ...state.customIngredients };
    delete customIngredients[ingredientId];
    // Exactly what `invert` will put back, from the same function, so the two
    // halves of the identity cannot describe different sets.
    const taken = cascadedLots(usage, cascade);
    if (taken.length === 0) return { ok: true, state: { ...state, customIngredients } };
    const inventory = { ...state.inventory };
    for (const lot of taken) delete inventory[lot.id];
    return { ok: true, state: { ...state, inventory, customIngredients } };
  },
  invert: (state, command) => {
    const item = state.customIngredients[command.payload.ingredientId];
    if (item === undefined) return null;
    const lots = cascadedLots(
      customIngredientUsage(state, item.id),
      command.payload.cascade ?? 'none',
    );
    return inverseCommand(
      command,
      'customIngredient.restore',
      lots.length === 0 ? { ingredient: item } : { ingredient: item, lots },
      item.updatedAt,
    );
  },
  summarize: (state, command) => {
    const name = quoted(itemName(state, command.payload.ingredientId));
    const lots = cascadedLots(
      customIngredientUsage(state, command.payload.ingredientId),
      command.payload.cascade ?? 'none',
    );
    return lots.length === 0
      ? `Remove custom ${name}`
      : `Remove custom ${name} and ${counted(lots.length, 'empty lot')}`;
  },
});

/**
 * Insert an entry verbatim — an upsert, and the inverse of
 * `customIngredient.remove` — together with any lots a cascade took.
 *
 * The lots are upserted as they were, timestamps and all, exactly as
 * `inventory.restore` would. The only producer of a `lots` array is the
 * cascade's inverse, so every lot in it is empty; the inverse computed here
 * relies on that when it answers a cascading remove.
 */
export const customIngredientRestore = defineCommand({
  type: 'customIngredient.restore',
  payload: p.customIngredientRestorePayloadSchema,
  apply: (state, command) => {
    const { ingredient, lots } = command.payload;
    const withEntry = putItem(state, ingredient);
    if (lots === undefined || lots.length === 0) return { ok: true, state: withEntry };
    const inventory = { ...state.inventory };
    for (const lot of lots) inventory[lot.id] = lot;
    return { ok: true, state: { ...withEntry, inventory } };
  },
  invert: (state, command) => {
    const { ingredient } = command.payload;
    const lots = command.payload.lots ?? [];
    const prior = state.customIngredients[ingredient.id];
    if (prior === undefined) {
      // Nothing was there, so a remove takes it away again; the lots that rode
      // along were the empty ones a cascade took, so the same cascade takes them.
      return inverseCommand(
        command,
        'customIngredient.remove',
        lots.length === 0
          ? { ingredientId: ingredient.id }
          : { ingredientId: ingredient.id, cascade: 'empty-lots' },
        command.timestamp,
      );
    }
    // The entry was there: put its prior document back, and the prior document
    // of every lot this restore overwrote. A lot this restore *added* has no
    // prior to put back and is left — no producer builds that payload.
    const priorLots = lots.flatMap((lot) => {
      const held = state.inventory[lot.id];
      return held === undefined ? [] : [held];
    });
    return inverseCommand(
      command,
      'customIngredient.restore',
      priorLots.length === 0 ? { ingredient: prior } : { ingredient: prior, lots: priorLots },
      prior.updatedAt,
    );
  },
  summarize: (_state, command) => {
    const lots = command.payload.lots ?? [];
    const name = quoted(command.payload.ingredient.name);
    return lots.length === 0
      ? `Restore custom ${name}`
      : `Restore custom ${name} and ${counted(lots.length, 'lot')}`;
  },
});
